// Buy 12 + Get 1 Free promotional bundle button. See snippets/product-promo-bundle.liquid.
//
// Extends the theme's own <product-form> element so the add-to-cart request, cart bubble,
// cart drawer, "product:added-to-cart" event, loading spinner and cart error messages all
// behave exactly like the regular Add to cart button. This file only adds:
//   - a guard against re-submitting while the request is in flight,
//   - copying the discount code to the clipboard on the customer's click,
//   - the confirmation message on the page and in the cart drawer.
// It never applies a discount or changes a price.

const TAG = 'product-promo-bundle-form';
const NOTICE_CLASS = 'product-promo-bundle-notice';
const COPY_TIMEOUT_MS = 1500;

let drawerNotice = null;

function removeDrawerNotice() {
  if (!drawerNotice) return;
  drawerNotice.observer.disconnect();
  drawerNotice.drawer.removeEventListener('hide', removeDrawerNotice);
  drawerNotice.drawer.querySelector(`.${NOTICE_CLASS}`)?.remove();
  drawerNotice = null;
}

// The drawer re-renders its content from the server after every cart change, so the
// notice is re-inserted whenever it disappears, until the customer closes the drawer.
function showDrawerNotice(html) {
  removeDrawerNotice();
  const drawer = document.querySelector('cart-modal');
  if (!drawer) return;

  const place = (announce) => {
    if (drawer.querySelector(`.${NOTICE_CLASS}`)) return;
    const header = drawer.querySelector('#CartModal-ScrollBody > .modal-header');
    if (!header) return;
    const notice = document.createElement('div');
    notice.className = NOTICE_CLASS;
    notice.setAttribute('role', 'status');
    header.after(notice);
    // Filling an already-present live region is what makes screen readers announce it.
    // setTimeout rather than requestAnimationFrame: rAF is paused in background tabs.
    if (announce) setTimeout(() => (notice.innerHTML = html), 100);
    else notice.innerHTML = html;
  };

  const observer = new MutationObserver(() => place(false));
  observer.observe(drawer, { childList: true, subtree: true });
  drawerNotice = { drawer, observer };
  drawer.addEventListener('hide', removeDrawerNotice);
  place(true);
}

customElements.whenDefined('product-form').then((ProductForm) => {
  if (customElements.get(TAG)) return;

  class ProductPromoBundleForm extends ProductForm {
    get statusRegion() {
      return this.querySelector('[data-promo-status]');
    }

    get variantId() {
      return this.form.querySelector('[name=id]').value;
    }

    // Resolves true only if the browser confirms the copy. Chrome can leave writeText
    // pending indefinitely in an unfocused window, so give up after a short wait and let
    // the customer see the code instead.
    copyCode() {
      const code = this.dataset.code;
      if (!code || !window.isSecureContext || !navigator.clipboard?.writeText) {
        return Promise.resolve(false);
      }
      try {
        const write = navigator.clipboard.writeText(code).then(
          () => true,
          () => false
        );
        const timeout = new Promise((resolve) => setTimeout(() => resolve(false), COPY_TIMEOUT_MS));
        return Promise.race([write, timeout]);
      } catch {
        return Promise.resolve(false);
      }
    }

    async showConfirmation(copied) {
      const html = this.messageHtml((await copied) ? 'copied' : 'manual');
      this.statusRegion.innerHTML = html;
      showDrawerNotice(html);
    }

    messageHtml(name) {
      return this.querySelector(`template[data-promo-message="${name}"]`)?.innerHTML.trim() ?? '';
    }

    async onSubmitHandler(event) {
      event.preventDefault();
      // The theme's spinner blocks pointer clicks only; this also blocks keyboard repeats.
      if (this.busy) return;
      this.busy = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.statusRegion.replaceChildren();
      removeDrawerNotice();

      // Must start inside the click, while the browser still treats it as a user action.
      const copied = this.copyCode();

      // Confirm as soon as the theme reports the add, rather than after the drawer has
      // finished animating open.
      const variantId = this.variantId;
      let added = false;
      const onAdded = (e) => {
        if (added || String(e.detail?.id) !== variantId) return;
        added = true;
        this.showConfirmation(copied);
      };
      document.addEventListener('product:added-to-cart', onAdded);
      this.error = undefined;

      try {
        await super.onSubmitHandler(event);
      } finally {
        document.removeEventListener('product:added-to-cart', onAdded);
        this.busy = false;
        this.submitButton.removeAttribute('aria-disabled');
      }

      if (!added && this.error !== true) {
        // Cart errors (e.g. not enough stock) are already shown by the theme. A network
        // failure is only logged by the theme, so give the customer something to act on.
        this.setErrorMessage(this.dataset.errorMessage || window.cartStrings?.error || '');
      }
    }
  }

  customElements.define(TAG, ProductPromoBundleForm);
});
